import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GuardianSidebar from '../components/GuardianSidebar';
import Toast, { useToast } from '../components/Toast';
import { useTheme } from '../context/ThemeContext';
import { useGuardianLanguage } from '../context/GuardianLanguageContext';
import translations from '../i18n/translations';
import DatePicker from '../components/DatePicker';
import { guardianFetch } from '../utils/authFetch';
import { logoutAndClearLocal } from '../api/authLogout';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function CircularProgress({ pct, size = 72, stroke = 6 }) {
  const { T } = useTheme();
  const r    = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const fill = (pct / 100) * circ;
  const color = pct >= 70 ? T.success : pct >= 40 ? T.warning : T.error;
  const textColor = pct === 0 ? T.textSecondary : color;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.border} strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${fill} ${circ - fill}`} strokeLinecap="round" />
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={13}
        fontWeight="700"
        fill={textColor}
        transform={`rotate(90, ${size / 2}, ${size / 2})`}
      >
        {pct === 0 ? '—' : `${pct}%`}
      </text>
    </svg>
  );
}

function ReadinessTag({ color, label }) {
  const { T } = useTheme();
  const dotColor = color === 'green' ? (T.success || '#10b981')
    : color === 'amber' ? (T.warning || '#f59e0b')
    : T.error;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: T.textSecondary }}>{label}</span>
    </div>
  );
}

function RecommendationCard({ subject, title, body }) {
  const { T } = useTheme();
  return (
    <div style={{
      borderLeft:   '4px solid #10b981',
      background:   '#10b98110',
      borderRadius: 12,
      padding:      '16px 20px',
      marginBottom: 12,
    }}>
      <div style={{ fontSize: 12, color: T.accent, fontWeight: 700, marginBottom: 4 }}>
        {subject}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.6 }}>
        {body}
      </div>
    </div>
  );
}

function SubjectCard({ subject, onClick }) {
  const { T }    = useTheme();
  const { lang } = useGuardianLanguage();
  const avg = Math.round(subject.avg_correct_percentage || 0);
  const [hovered, setHovered] = useState(false);

  const readinessLabels = {
    'جاهز':          { en: 'Ready',       ar: 'جاهز' },
    'يحتاج مراجعة': { en: 'Needs Review', ar: 'يحتاج مراجعة' },
    'غير جاهز':      { en: 'Not Ready',    ar: 'غير جاهز' },
  };
  const readinessDisplay = readinessLabels[subject.readiness_label]?.[lang]
    ?? subject.readiness_label;

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA' : 'en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      }).format(new Date(iso));
    } catch { return iso?.slice(0, 10); }
  };

  return (
    <div
      style={{
        background: T.bgCardSolid, border: `1px solid ${T.border}`,
        borderRadius: 14, padding: 20,
        display: 'flex', flexDirection: 'column', gap: 12,
        transition: 'background 0.2s ease, box-shadow 0.2s ease',
        cursor: 'pointer',
        boxShadow: hovered ? '0 8px 32px rgba(5,150,105,0.22)' : 'none',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary }}>{subject.subject_name}</div>
          <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 4 }}>
            {subject.sessions_count} {lang === 'ar' ? 'جلسة' : 'sessions'}
          </div>
        </div>
        <CircularProgress pct={avg} size={64} />
      </div>

      <ReadinessTag color={subject.readiness_color} label={readinessDisplay} />

      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: T.textSecondary, flexWrap: 'wrap' }}>
        <span>{subject.weakness_count} {lang === 'ar' ? 'نقطة ضعف' : 'weaknesses'}</span>
        {subject.last_session_date && (
          <span>{lang === 'ar' ? 'آخر جلسة:' : 'Last:'} {formatDate(subject.last_session_date)}</span>
        )}
      </div>
    </div>
  );
}

function generateRecommendations(student, overall, subjects, lang) {
  const name            = student.first_name;
  const avg             = overall.avg_performance;
  const totalSessions   = overall.total_sessions;
  const totalWeaknesses = overall.total_weaknesses;
  const daysToExam      = overall.days_to_exam;
  const lastSessionDate = overall.last_session_date;

  const daysSince = lastSessionDate
    ? Math.max(0, Math.floor((Date.now() - new Date(lastSessionDate).getTime()) / 86400000))
    : null;

  const subjectWithMostWeaknesses = subjects.length > 0
    ? [...subjects].sort((a, b) => b.weakness_count - a.weakness_count)[0]
    : null;

  const recs = [];

  if (totalSessions === 0) {
    recs.push({
      color: 'blue',
      text: lang === 'ar'
        ? `👋 ${name} لم يبدأ بعد. شجّعه على بدء أول جلسة — البداية هي الخطوة الأصعب!`
        : `👋 ${name} hasn't started yet. Encourage them to begin their first session — starting is always the hardest step!`,
    });
  }

  if (avg >= 80 && totalSessions >= 5 && totalWeaknesses === 0) {
    recs.push({
      color: 'green',
      text: lang === 'ar'
        ? `🌟 أداء ممتاز! ${name} يحقق نتائج رائعة. احرص على مكافأته وتشجيعه للحفاظ على هذا المستوى.`
        : `🌟 Excellent performance! ${name} is achieving great results. Make sure to reward and encourage them to maintain this level.`,
    });
  }

  if (totalSessions >= 3 && avg >= 60 && avg < 80) {
    recs.push({
      color: 'blue',
      text: lang === 'ar'
        ? `📈 ${name} يتحسن بشكل ملحوظ. استمر في التشجيع اليومي وحافظ على انتظام الجلسات.`
        : `📈 ${name} is showing notable improvement. Keep up the daily encouragement and maintain session consistency.`,
    });
  }

  if (totalWeaknesses >= 3) {
    const subjectName = subjectWithMostWeaknesses?.subject_name || '';
    recs.push({
      color: 'amber',
      text: lang === 'ar'
        ? `⚠️ يعاني ${name} من بعض نقاط الضعف في ${subjectName}. يُنصح بجلسات مراجعة إضافية في هذه المادة.`
        : `⚠️ ${name} has some weak points in ${subjectName}. Additional review sessions in this subject are recommended.`,
    });
  }

  if (totalSessions > 0 && daysSince !== null && daysSince > 3) {
    recs.push({
      color: 'red',
      text: lang === 'ar'
        ? `⏰ لم يمارس ${name} أي جلسة منذ ${daysSince} أيام. حاول تحفيزه وتذكيره بمواعيد مذاكرته.`
        : `⏰ ${name} hasn't had a session in ${daysSince} days. Try to motivate and remind them of their study schedule.`,
    });
  }

  if (daysToExam !== null && daysToExam !== undefined && daysToExam <= 7) {
    recs.push({
      color: 'orange',
      text: lang === 'ar'
        ? `📅 امتحان ${name} بعد ${daysToExam} أيام فقط! جهبذ فعّل وضع المراجعة المكثفة تلقائياً للمساعدة في التحضير.`
        : `📅 ${name}'s exam is in only ${daysToExam} days! Jahbath has automatically activated Intensive Review Mode to help with preparation.`,
    });
  }

  return recs;
}

function EditStudentModal({ student, onClose, onSaved, showToast }) {
  const { T }    = useTheme();
  const { lang } = useGuardianLanguage();
  const tr = translations[lang];

  const [form, setForm] = useState({
    first_name:         student.first_name,
    grade:              String(student.grade),
    school_name:        student.school_name || '',
    ict_or_cs:          student.ict_or_cs || '',
    islamic_lang:       student.islamic_lang || '',
    arabic_lang:        student.arabic_lang || '',
    qatar_history_lang: student.qatar_history_lang || '',
    midterm_exam_date:  student.midterm_exam_date || '',
    final_exam_date:    student.final_exam_date   || '',
  });
  const [monthlyDates, setMonthlyDates] = useState(
    (student.monthly_exam_dates || []).map(d => (typeof d === 'string' ? d : d.date || ''))
  );
  const [dateErrors, setDateErrors] = useState({});
  const [loading,    setLoading]    = useState(false);

  const grade   = parseInt(form.grade, 10);
  const isQatar = (student.country || '').toLowerCase().includes('q');
  const set     = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validateDates = () => {
    const errs = {};
    if (!form.midterm_exam_date) errs.midterm = tr.required;
    if (!form.final_exam_date)   errs.final   = tr.required;
    if (form.midterm_exam_date && form.final_exam_date && form.final_exam_date <= form.midterm_exam_date) {
      errs.final = tr.dateOrderError;
    }
    const monthly = {};
    monthlyDates.forEach((d, i) => {
      if (d && form.midterm_exam_date && form.final_exam_date) {
        if (d <= form.midterm_exam_date || d >= form.final_exam_date) {
          monthly[i] = tr.monthlyDateRangeError;
        }
      }
    });
    if (Object.keys(monthly).length) errs.monthly = monthly;
    return errs;
  };

  const handleSave = async () => {
    const errs = validateDates();
    if (Object.keys(errs).length) { setDateErrors(errs); return; }
    setLoading(true); setDateErrors({});
    try {
      const res  = await guardianFetch(`${API}/guardian/update-student`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id:         student.id,
          first_name:         form.first_name.trim(),
          grade,
          school_name:        form.school_name.trim() || null,
          ict_or_cs:          (grade >= 7 && grade <= 9) ? (form.ict_or_cs || null) : null,
          islamic_lang:       isQatar ? (form.islamic_lang || null) : null,
          arabic_lang:        isQatar ? (form.arabic_lang  || null) : null,
          qatar_history_lang: isQatar ? (form.qatar_history_lang || null) : null,
          midterm_exam_date:  form.midterm_exam_date || null,
          final_exam_date:    form.final_exam_date   || null,
          monthly_exam_dates: monthlyDates.filter(d => d),
        }),
      });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        showToast(lang === 'ar' ? 'تم تحديث بيانات الطالب ✓' : 'Student updated ✓');
        onSaved();
      } else {
        showToast(lang === 'ar' ? 'حدث خطأ، حاول مرة أخرى' : 'An error occurred', 'error');
      }
    } catch {
      showToast(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Server connection error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px',
    background: T.bgInput, border: `1px solid ${T.borderInput}`,
    borderRadius: 10, color: T.textPrimary, fontSize: 14,
    boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', marginBottom: 14,
  };
  const labelStyle = { fontSize: 13, color: T.textSecondary, marginBottom: 7, display: 'block', fontWeight: 500 };

  const LABELS = {
    firstName:        { ar: 'الاسم الأول',               en: 'First Name'              },
    gradeLabel:       { ar: 'الصف الدراسي',              en: 'Grade'                   },
    schoolName:       { ar: 'اسم المدرسة (اختياري)',     en: 'School Name (optional)'  },
    ictLabel:         { ar: 'يدرس ICT أو علوم الحاسوب',  en: 'Studies ICT or CS'       },
    islamicLang:      { ar: 'لغة التربية الإسلامية',     en: 'Islamic Studies Language'},
    arabicLang:       { ar: 'لغة اللغة العربية',         en: 'Arabic Language'         },
    qatarHistoryLang: { ar: 'لغة تاريخ قطر',            en: 'Qatar History Language'  },
    arabic:           { ar: 'عربي',                      en: 'Arabic'                  },
    english:          { ar: 'إنجليزي',                   en: 'English'                 },
  };
  const L = (k) => LABELS[k][lang];

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 18, padding: 32, width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: T.textPrimary, marginBottom: 22 }}>
          {lang === 'ar' ? 'تعديل بيانات الطالب' : 'Edit Student'}
        </div>

        <label style={labelStyle}>{L('firstName')}</label>
        <input style={inputStyle} dir="auto" value={form.first_name} onChange={e => set('first_name', e.target.value)} />

        <label style={labelStyle}>{L('gradeLabel')}</label>
        <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.grade} onChange={e => set('grade', e.target.value)}>
          {Array.from({ length: 12 }, (_, i) => i + 1).map(g => (
            <option key={g} value={g}>{lang === 'ar' ? 'الصف' : 'Grade'} {g}</option>
          ))}
        </select>

        <label style={labelStyle}>{L('schoolName')}</label>
        <input style={inputStyle} dir="auto" value={form.school_name} onChange={e => set('school_name', e.target.value)} />

        {grade >= 7 && grade <= 9 && (
          <>
            <label style={labelStyle}>{L('ictLabel')}</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.ict_or_cs} onChange={e => set('ict_or_cs', e.target.value)}>
              <option value="">— {lang === 'ar' ? 'لا ينطبق' : 'Not applicable'} —</option>
              <option value="ict">ICT</option>
              <option value="cs">{lang === 'ar' ? 'علوم الحاسوب' : 'Computer Science'}</option>
            </select>
          </>
        )}

        {isQatar && (
          <>
            <label style={labelStyle}>{L('islamicLang')}</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.islamic_lang} onChange={e => set('islamic_lang', e.target.value)}>
              <option value="">— {lang === 'ar' ? 'اختر' : 'Choose'} —</option>
              <option value="ar">{L('arabic')}</option>
              <option value="en">{L('english')}</option>
            </select>

            <label style={labelStyle}>{L('arabicLang')}</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.arabic_lang} onChange={e => set('arabic_lang', e.target.value)}>
              <option value="">— {lang === 'ar' ? 'اختر' : 'Choose'} —</option>
              <option value="ar">{L('arabic')}</option>
              <option value="en">{L('english')}</option>
            </select>

            <label style={labelStyle}>{L('qatarHistoryLang')}</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.qatar_history_lang} onChange={e => set('qatar_history_lang', e.target.value)}>
              <option value="">— {lang === 'ar' ? 'اختر' : 'Choose'} —</option>
              <option value="ar">{L('arabic')}</option>
              <option value="en">{L('english')}</option>
            </select>
          </>
        )}

        <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 8, paddingTop: 20, marginBottom: 4 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.accent, marginBottom: 16 }}>{tr.examDates}</div>

          <label style={labelStyle}>{tr.midterm} <span style={{ color: T.error }}>*</span></label>
          <DatePicker
            value={form.midterm_exam_date}
            onChange={v => set('midterm_exam_date', v)}
            placeholder={lang === 'ar' ? 'اختر تاريخاً' : 'Select a date'}
          />
          {dateErrors.midterm && <div style={{ fontSize: 12, color: T.error, marginTop: -10, marginBottom: 10 }}>{dateErrors.midterm}</div>}

          <label style={labelStyle}>{tr.finalExam} <span style={{ color: T.error }}>*</span></label>
          <DatePicker
            value={form.final_exam_date}
            onChange={v => set('final_exam_date', v)}
            placeholder={lang === 'ar' ? 'اختر تاريخاً' : 'Select a date'}
          />
          {dateErrors.final && <div style={{ fontSize: 12, color: T.error, marginTop: -10, marginBottom: 10 }}>{dateErrors.final}</div>}

          <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 10, fontWeight: 500 }}>{tr.monthlyTests}</div>
          {monthlyDates.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <DatePicker
                  value={d}
                  onChange={v => {
                    const next = [...monthlyDates];
                    next[i] = v;
                    setMonthlyDates(next);
                  }}
                  placeholder={lang === 'ar' ? 'اختر تاريخاً' : 'Select a date'}
                  wrapperStyle={{ marginBottom: 0 }}
                />
                {dateErrors.monthly?.[i] && (
                  <div style={{ fontSize: 12, color: T.error, marginTop: 4 }}>{dateErrors.monthly[i]}</div>
                )}
              </div>
              <button
                onClick={() => setMonthlyDates(monthlyDates.filter((_, j) => j !== i))}
                style={{ padding: '8px 12px', background: 'transparent', border: `1px solid ${T.error}`, color: T.error, borderRadius: 8, cursor: 'pointer', fontSize: 14, fontFamily: 'inherit', flexShrink: 0 }}
              >✕</button>
            </div>
          ))}
          {monthlyDates.length < 12 && (
            <button
              onClick={() => setMonthlyDates([...monthlyDates, ''])}
              style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${T.border}`, color: T.accent, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}
            >{tr.addDate}</button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button
            style={{ flex: 1, padding: '11px 0', background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(5,150,105,0.3)' }}
            onClick={handleSave} disabled={loading}
          >
            {loading ? '...' : tr.save}
          </button>
          <button
            style={{ flex: 1, padding: '11px 0', background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={onClose}
          >
            {tr.cancel}
          </button>
        </div>
      </div>
    </div>
  );
}

function SubjectDetailPanel({ subject, recommendations, lang, onClose }) {
  const { T } = useTheme();

  const filteredSessions = subject.recent_sessions || [];
  const filteredRecs     = (recommendations || []).filter(r => r.subject === subject.subject_name);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA' : 'en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      }).format(new Date(iso));
    } catch { return iso?.slice(0, 10) || ''; }
  };

  const labels = {
    sessions:        lang === 'ar' ? 'الجلسات الأخيرة'       : 'Recent Sessions',
    recs:            lang === 'ar' ? 'توصيات تربوية'          : 'Pedagogical Recommendations',
    weaknesses:      lang === 'ar' ? 'نقاط تحتاج مراجعة'      : 'Areas Needing Review',
    noSessions:      lang === 'ar' ? 'لا توجد جلسات لهذه المادة' : 'No sessions yet for this subject',
    noRecs:          lang === 'ar' ? 'لا توجد توصيات بعد'      : 'No recommendations yet',
    noWeaknesses:    lang === 'ar' ? 'لا توجد نقاط ضعف لهذه المادة ✓' : 'No areas need review for this subject ✓',
    weaknessesFound: lang === 'ar'
      ? `هذه المادة تحتوي على ${subject.weakness_count} نقطة تحتاج مراجعة.`
      : `This subject has ${subject.weakness_count} area(s) that need review.`,
    unit:            lang === 'ar' ? 'الوحدة' : 'Unit',
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: T.bgCardSolid, maxWidth: 860, width: '90%', maxHeight: '85vh', borderRadius: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: 24, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1E3A5F', fontFamily: 'Sora, Segoe UI, system-ui, sans-serif' }}>
            {subject.subject_name}
          </div>
          <button
            onClick={onClose}
            style={{ width: 32, height: 32, borderRadius: '50%', border: '1px solid #E5E7EB', background: 'white', cursor: 'pointer', fontSize: 18, color: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 32 }}>

          {/* Section 1 — Recent Sessions */}
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, marginBottom: 12 }}>{labels.sessions}</div>
            {filteredSessions.length === 0 ? (
              <div style={{ fontSize: 13, color: T.textSecondary }}>{labels.noSessions}</div>
            ) : (
              filteredSessions.map((sess, i) => (
                <div key={sess.id || i} style={{ padding: '12px 0', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 14, color: T.textPrimary, fontWeight: 500 }}>
                    {sess.unit || '—'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
                    {sess.score != null ? (
                      <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 20, background: sess.score >= 60 ? '#D1FAE5' : '#FEF3C7', color: sess.score >= 60 ? '#059669' : '#D97706' }}>
                        {Math.round(sess.score)}%
                      </span>
                    ) : (
                      <span style={{ fontSize: 13, color: T.textSecondary }}>—</span>
                    )}
                    <span style={{ fontSize: 12, color: T.textSecondary }}>{formatDate(sess.created_at || sess.started_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Section 2 — Pedagogical Recommendations */}
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, marginBottom: 12 }}>{labels.recs}</div>
            {filteredRecs.length === 0 ? (
              <div style={{ fontSize: 13, color: T.textSecondary }}>{labels.noRecs}</div>
            ) : (
              filteredRecs.map((r, i) => (
                <div key={i} style={{ background: '#F0FDF4', borderRadius: 12, padding: 16, marginBottom: 8 }}>
                  <span style={{ background: '#D1FAE5', color: '#059669', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                    {r.subject}
                  </span>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#1E3A5F', marginTop: 6 }}>{r.title}</div>
                  <div style={{ fontSize: 14, color: '#374151', marginTop: 4, lineHeight: 1.6 }}>{r.body}</div>
                </div>
              ))
            )}
          </div>

          {/* Section 3 — Weaknesses */}
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, marginBottom: 12 }}>{labels.weaknesses}</div>
            {(subject.weakness_count || 0) === 0 ? (
              <div style={{ fontSize: 13, color: '#059669' }}>{labels.noWeaknesses}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(subject.weaknesses || []).map((w, i) => (
                  <div key={i} style={{
                    background: '#FFFBEB',
                    border: '1px solid #FDE68A',
                    borderRadius: 10,
                    padding: '10px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                  }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#92400E' }}>
                      {w.topic || '—'}
                    </div>
                    {w.weakness_type && (
                      <div style={{ fontSize: 11, color: '#D97706', textTransform: 'capitalize' }}>
                        {w.weakness_type}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  const { T } = useTheme();
  const skelBg = T.bg === '#f0f4f8' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';
  return (
    <>
      <style>{`@keyframes sd-pulse{0%,100%{opacity:.4}50%{opacity:.8}}`}</style>
      {[200, 120, 300, 200].map((h, i) => (
        <div key={i} style={{ height: h, borderRadius: 16, marginBottom: 24, background: skelBg, animation: 'sd-pulse 1.6s ease-in-out infinite' }} />
      ))}
    </>
  );
}

export default function StudentDetail() {
  const { student_id } = useParams();
  const navigate = useNavigate();
  const { T }    = useTheme();
  const { lang } = useGuardianLanguage();
  const tr  = translations[lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  const { toast, show: showToast } = useToast();

  const token      = localStorage.getItem('jahbath_token');
  const guardianId = localStorage.getItem('jahbath_guardian_id');

  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene  = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 30;
    const meshes = [];
    const geos = [new THREE.IcosahedronGeometry(1, 0), new THREE.OctahedronGeometry(1, 0), new THREE.TetrahedronGeometry(1, 0)];
    for (let i = 0; i < 18; i++) {
      const geo = geos[i % 3];
      const mat = new THREE.MeshBasicMaterial({ color: new THREE.Color(`hsl(${150 + (i * 12)},60%,${60 + (i % 4) * 8}%)`), wireframe: true, transparent: true, opacity: 0.18 + (i % 5) * 0.04 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set((Math.random() - 0.5) * 60, (Math.random() - 0.5) * 40, (Math.random() - 0.5) * 20);
      mesh.scale.setScalar(0.8 + Math.random() * 2.2);
      mesh.userData = { rx: (Math.random() - 0.5) * 0.008, ry: (Math.random() - 0.5) * 0.008 };
      scene.add(mesh); meshes.push(mesh);
    }
    const pts = new Float32Array(120 * 3);
    for (let i = 0; i < 120 * 3; i++) pts[i] = (Math.random() - 0.5) * 80;
    const pGeo = new THREE.BufferGeometry(); pGeo.setAttribute('position', new THREE.BufferAttribute(pts, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x10B981, size: 0.18, transparent: true, opacity: 0.5 });
    scene.add(new THREE.Points(pGeo, pMat));
    const linePositions = [];
    for (let i = 0; i < 18; i++) { for (let j = i + 1; j < 18; j++) { if (meshes[i].position.distanceTo(meshes[j].position) < 18) { linePositions.push(meshes[i].position.x, meshes[i].position.y, meshes[i].position.z, meshes[j].position.x, meshes[j].position.y, meshes[j].position.z); } } }
    const lGeo = new THREE.BufferGeometry(); lGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(linePositions), 3));
    scene.add(new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x059669, transparent: true, opacity: 0.08 })));
    let mx = 0, my = 0;
    const onMouse = e => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = -(e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMouse);
    const onResize = () => { camera.aspect = window.innerWidth / window.innerHeight; camera.updateProjectionMatrix(); renderer.setSize(window.innerWidth, window.innerHeight); };
    window.addEventListener('resize', onResize);
    let rafId; const animate = () => { rafId = requestAnimationFrame(animate); meshes.forEach(m => { m.rotation.x += m.userData.rx; m.rotation.y += m.userData.ry; }); camera.position.x += (mx * 2 - camera.position.x) * 0.03; camera.position.y += (my * 2 - camera.position.y) * 0.03; renderer.render(scene, camera); };
    animate();
    return () => { cancelAnimationFrame(rafId); window.removeEventListener('mousemove', onMouse); window.removeEventListener('resize', onResize); renderer.dispose(); };
  }, []);

  const [data,            setData]            = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState('');
  const [editOpen,        setEditOpen]        = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);

  useEffect(() => {
    if (!token || !guardianId) { navigate('/guardian/auth', { replace: true }); return; }
    loadDetail();
  }, [student_id]); // eslint-disable-line

  const loadDetail = async () => {
    setLoading(true); setError('');
    try {
      const res  = await guardianFetch(`${API}/guardian/student/${student_id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res) return;
      const json = await res.json();
      if (json.success) { setData(json); }
      else { setError(json.error || (lang === 'ar' ? 'تعذر تحميل البيانات' : 'Failed to load')); }
    } catch {
      setError(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Cannot connect to server');
    } finally { setLoading(false); }
  };

  const handleLogout = async () => {
    await logoutAndClearLocal('guardian');
    navigate('/guardian/auth', { replace: true });
  };

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA' : 'en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      }).format(new Date(iso));
    } catch { return iso?.slice(0, 10); }
  };

  if (!token || !guardianId) return null;

  const card = {
    background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)',
    border: `1px solid ${T.border}`,
    borderRadius: 18, padding: '24px 28px', marginBottom: 24,
    boxShadow: '0 4px 20px rgba(5,150,105,0.10)',
    transition: 'background 0.2s ease',
  };
  const sectionTitle = { fontSize: 18, fontWeight: 700, color: T.textPrimary, marginBottom: 16 };

  const recommendations = data ? (data.pedagogical_recommendations || []) : [];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'transparent', color: T.textPrimary, fontFamily: "'Sora', sans-serif", direction: dir, transition: 'background 0.2s ease' }}>
      <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }} />
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&family=Nunito:wght@400;600;700&display=swap');`}</style>
      <GuardianSidebar guardianName={null} guardianEmail={null} onLogout={handleLogout} />

      <main style={{ flex: 1, padding: '32px 36px', overflowY: 'auto', position: 'relative', zIndex: 10 }}>
        <button
          onClick={() => navigate('/guardian/dashboard')}
          style={{ background: 'none', border: 'none', color: T.accent, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 24, padding: 0 }}
        >
          → {lang === 'ar' ? 'العودة للوحة التحكم' : 'Back to Dashboard'}
        </button>

        {loading ? <LoadingSkeleton /> : error ? (
          <div style={{ textAlign: 'center', paddingTop: 80 }}>
            <div style={{ color: T.error, fontSize: 16, marginBottom: 16 }}>{error}</div>
            <button onClick={loadDetail} style={{ padding: '11px 28px', background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(5,150,105,0.3)' }}>
              {tr.retryBtn}
            </button>
          </div>
        ) : data && (
          <>
            <div style={{ fontSize: 26, fontWeight: 700, color: T.textPrimary, marginBottom: 28 }}>
              {data.student.first_name}
            </div>

            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={sectionTitle}>{lang === 'ar' ? 'نظرة عامة' : 'Overview'}</div>
                <button
                  onClick={() => setEditOpen(true)}
                  style={{ padding: '8px 20px', background: 'transparent', border: `1px solid ${T.border}`, color: T.textPrimary, borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {tr.edit}
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 20, marginBottom: 24 }}>
                {[
                  { label: lang === 'ar' ? 'الاسم'   : 'Name',    value: data.student.first_name },
                  { label: lang === 'ar' ? 'الصف'    : 'Grade',   value: lang === 'ar' ? `الصف ${data.student.grade}` : `Grade ${data.student.grade}` },
                  { label: lang === 'ar' ? 'الدولة'  : 'Country', value: data.student.country === 'qa' ? '🇶🇦 Qatar' : `🌍 ${data.student.country}` },
                  { label: lang === 'ar' ? 'المدرسة' : 'School',  value: data.student.school_name || '—' },
                ].map((f, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>{f.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>{f.value}</div>
                  </div>
                ))}
              </div>

              {(data.student.midterm_exam_date || data.student.final_exam_date || (data.student.monthly_exam_dates || []).length > 0) && (
                <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 20 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.accent, marginBottom: 14 }}>{tr.examDates}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 14 }}>
                    {data.student.midterm_exam_date && (
                      <div>
                        <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>{tr.midterm}</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>{formatDate(data.student.midterm_exam_date)}</div>
                      </div>
                    )}
                    {data.student.final_exam_date && (
                      <div>
                        <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 4 }}>{tr.finalExam}</div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>{formatDate(data.student.final_exam_date)}</div>
                      </div>
                    )}
                  </div>
                  {(data.student.monthly_exam_dates || []).length > 0 && (
                    <div>
                      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 8 }}>{tr.monthlyTests}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {data.student.monthly_exam_dates.map((d, i) => {
                          const dateStr = typeof d === 'string' ? d : d.date;
                          return (
                            <div key={i} style={{ padding: '4px 14px', borderRadius: 20, background: `${T.accent}18`, color: T.accent, border: `1px solid ${T.accent}40`, fontSize: 13 }}>
                              {formatDate(dateStr)}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 28 }}>
              <div style={sectionTitle}>{lang === 'ar' ? 'الأداء لكل مادة' : 'Performance by Subject'}</div>
              {data.subjects_performance.length === 0 ? (
                <div style={{ color: T.textSecondary, fontSize: 14, padding: '24px 0' }}>
                  {lang === 'ar' ? 'لم يبدأ الطالب أي جلسة بعد' : "Student hasn't started any sessions yet"}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                  {data.subjects_performance.map(s => (
                    <SubjectCard
                      key={s.subject_id}
                      subject={s}
                      onClick={() => setSelectedSubject(s)}
                    />
                  ))}
                </div>
              )}
            </div>

          </>
        )}
      </main>

      {selectedSubject && data && (
        <SubjectDetailPanel
          subject={selectedSubject}
          recommendations={data.pedagogical_recommendations || []}
          lang={lang}
          onClose={() => setSelectedSubject(null)}
        />
      )}

      {editOpen && data && (
        <EditStudentModal
          student={data.student}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); loadDetail(); }}
          showToast={showToast}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}
